// The quote gate. This is the component the model layer is worth having.
//
// A model asked to find clauses will sometimes produce a clause that is not in
// the document: a paraphrase, a tidied-up version, a sentence from a plan it has
// seen before, or an outright invention. Every one of those is fatal to a tool
// whose entire claim is that each finding can be checked against the source.
//
// So no model output becomes a finding until its quote has been located in the
// document. What survives is not the model's text but the document's: the
// accepted quote is re-sliced out of doc.rawText at the position found, so a
// reviewer pressing Ctrl-F in the PDF finds it character for character.
//
// Candidates that fail are dropped and counted. The count is shown to the user,
// because "3 candidate findings were dropped because their quoted text was not
// found in the document" tells a reviewer exactly how much to trust the rest.

import type { FailureModeId, Flag, SourceDocument } from '../../types';
import { TAXONOMY, detectionSignal, evidenceFor, tierOf } from '../../data/taxonomy';
import { buildLocator, sectionRefOf } from '../deterministic/locate';
import { findWitnessingLanguage } from '../deterministic/generationCheck';
import { enrichFlag } from '../finding';

export const CHECK_ID = 'model-classification';

/** What the model is allowed to return. Everything else it says is discarded. */
export interface ModelCandidate {
  failureModeId: string;
  fires: boolean;
  quote: string | null;
  basisIndex: number | null;
}

export type DropReason =
  | 'quote not found in document'
  | 'no quote supplied'
  | 'quote too short to identify a clause'
  | 'quote longer than a clause'
  | 'failure mode not in this layer'
  | 'reported as not firing'
  | 'FM-03 requires both of its two conditions';

export interface DroppedCandidate {
  failureModeId: string;
  quote: string | null;
  reason: DropReason;
  /** Which normalisation passes were attempted, for the expandable detail. */
  attempts?: string[];
}

export interface GateResult {
  accepted: Flag[];
  dropped: DroppedCandidate[];
}

/** Failure modes the deterministic layer cannot reach. */
export const MODEL_LAYER_FAILURE_MODES: FailureModeId[] = [
  'FM-01',
  'FM-03',
  'FM-05',
  'FM-06',
  'FM-07',
  'FM-09',
  'FM-10',
  'FM-11',
  'FM-12',
  'FM-13',
  'FM-14',
];

const MIN_QUOTE_CHARS = 12;
const MAX_QUOTE_CHARS = 2000;

/* ==========================================================================
   Normalisation, with a map back to the original offsets
   ========================================================================== */

/**
 * Characters folded on the second pass. Only differences a PDF text layer
 * introduces or a model tidies away — never anything that changes what the
 * clause says.
 */
const PUNCTUATION_FOLDS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '′': "'",
  '″': '"',
  '‐': '-',
  '‑': '-',
  '‒': '-',
  '–': '-',
  '—': '-',
  '―': '-',
  '−': '-',
  '…': '...',
  ' ': ' ',
  ' ': ' ',
  ' ': ' ',
  '​': '',
  '­': '', // soft hyphen
  'ﬀ': 'ff',
  'ﬁ': 'fi',
  'ﬂ': 'fl',
  'ﬃ': 'ffi',
  'ﬄ': 'ffl',
  'ﬅ': 'st',
  'ﬆ': 'st',
};

interface Folded {
  text: string;
  /** map[i] is the index in the source string that produced output char i. */
  map: number[];
}

/**
 * Collapses whitespace runs to a single space and, at level 2, folds the
 * punctuation above. The map records where every surviving character came from,
 * so a match in folded space can be turned back into an exact range in the
 * original text.
 */
function fold(source: string, level: 1 | 2): Folded {
  let text = '';
  const map: number[] = [];
  let inWhitespace = false;

  for (let i = 0; i < source.length; i++) {
    const char = source[i];

    if (/\s/.test(char)) {
      if (!inWhitespace) {
        text += ' ';
        map.push(i);
        inWhitespace = true;
      }
      continue;
    }
    inWhitespace = false;

    const replacement = level === 2 ? (PUNCTUATION_FOLDS[char] ?? char) : char;
    for (const out of replacement) {
      text += out;
      map.push(i);
    }
  }

  return { text, map };
}

function trimFolded(value: string): string {
  return value.trim();
}

export interface QuoteMatch {
  /** The document's own text at the located position. */
  verbatim: string;
  start: number;
  end: number;
  /** Which pass found it. */
  pass: 'exact' | 'whitespace' | 'punctuation';
}

/**
 * Locates a quote in the document and returns the document's text, not the
 * model's. Three passes, cheapest first; anything still unfound is a miss.
 */
export function locateQuote(
  rawText: string,
  quote: string,
  cache?: { level1?: Folded; level2?: Folded },
): QuoteMatch | null {
  // 1 — exact.
  const exactAt = rawText.indexOf(quote);
  if (exactAt >= 0) {
    return {
      verbatim: rawText.slice(exactAt, exactAt + quote.length),
      start: exactAt,
      end: exactAt + quote.length,
      pass: 'exact',
    };
  }

  // 2 — whitespace normalised on both sides. A PDF text layer breaks lines
  // mid-sentence, so a correctly copied quote often differs only in spacing.
  const doc1 = cache?.level1 ?? fold(rawText, 1);
  if (cache) cache.level1 = doc1;
  const q1 = trimFolded(fold(quote, 1).text);
  if (q1.length > 0) {
    const at = doc1.text.indexOf(q1);
    if (at >= 0) {
      const start = doc1.map[at];
      const end = doc1.map[at + q1.length - 1] + 1;
      return { verbatim: rawText.slice(start, end), start, end, pass: 'whitespace' };
    }
  }

  // 3 — punctuation and ligatures folded as well.
  const doc2 = cache?.level2 ?? fold(rawText, 2);
  if (cache) cache.level2 = doc2;
  const q2 = trimFolded(fold(quote, 2).text);
  if (q2.length > 0) {
    const at = doc2.text.indexOf(q2);
    if (at >= 0) {
      const start = doc2.map[at];
      const end = doc2.map[at + q2.length - 1] + 1;
      return { verbatim: rawText.slice(start, end), start, end, pass: 'punctuation' };
    }
  }

  return null;
}

/* ==========================================================================
   FM-03 — the two-fact join
   ========================================================================== */

/** An O&M contractor, subcontractor or third-party provider in the structure. */
const THIRD_PARTY = [
  /\bO&M\s+(?:contractor|subcontractor|provider|firm|vendor)\b/i,
  /\b(?:operations?\s+and\s+maintenance)\s+(?:contractor|subcontractor|provider)\b/i,
  /\bsub-?contractor\b/i,
  /\bthird[-\s]party\s+(?:service\s+)?(?:provider|contractor|firm)\b/i,
  /\bservice\s+provider\b/i,
  /\bmaintenance\s+contractor\b/i,
];

/** Language that constrains who may witness. */
const EMPLOYER_CONSTRAINT = [
  /\b(?:government|federal|agency)\s+employee\b/i,
  /\bemployee\s+of\s+the\s+(?:government|agency|United States)\b/i,
  /\bshall\s+not\s+be\s+an\s+employee\s+of\s+(?:the\s+)?(?:contractor|ESCO)\b/i,
  /\bmay\s+not\s+be\s+(?:an\s+)?(?:contractor|subcontractor)\s+personnel\b/i,
  /\bindependent\s+of\s+the\s+(?:contractor|ESCO)\b/i,
  /\bexclud(?:es|ing)\s+contractor\s+(?:and\s+subcontractor\s+)?personnel\b/i,
];

export interface Fm03Conditions {
  thirdPartyPresent: boolean;
  thirdPartyEvidence: string | null;
  witnessRoleDescribed: boolean;
  witnessEvidence: string | null;
  employerConstrained: boolean;
  employerEvidence: string | null;
  /** Both conditions hold. */
  fires: boolean;
}

/**
 * FM-03 fires only on a join of two facts, never on one. A plan that never
 * constrains the witness role is not by itself a finding — nearly every plan
 * fails that way, which is why the worked pre-mortem called catching it "the
 * weakest kind of catch". It becomes a real detection when a third party who
 * benefits from the measurement is also in the project structure.
 */
export function evaluateFm03(doc: SourceDocument): Fm03Conditions {
  const text = doc.rawText;

  let thirdPartyEvidence: string | null = null;
  for (const pattern of THIRD_PARTY) {
    const m = pattern.exec(text);
    if (m) {
      thirdPartyEvidence = m[0];
      break;
    }
  }

  const witness = findWitnessingLanguage(doc);

  let employerEvidence: string | null = null;
  for (const pattern of EMPLOYER_CONSTRAINT) {
    const m = pattern.exec(text);
    if (m) {
      employerEvidence = m[0];
      break;
    }
  }

  const thirdPartyPresent = thirdPartyEvidence !== null;
  const witnessRoleDescribed = witness !== null;
  const employerConstrained = employerEvidence !== null;

  return {
    thirdPartyPresent,
    thirdPartyEvidence,
    witnessRoleDescribed,
    witnessEvidence: witness ? witness.matched : null,
    employerConstrained,
    employerEvidence,
    fires: thirdPartyPresent && witnessRoleDescribed && !employerConstrained,
  };
}

/* ==========================================================================
   The gate
   ========================================================================== */

interface GateOptions {
  /** Restrict to the failure modes this layer owns. */
  allowed?: FailureModeId[];
}

export function runQuoteGate(
  doc: SourceDocument,
  candidates: ModelCandidate[],
  options: GateOptions = {},
): GateResult {
  const allowed = new Set<FailureModeId>(options.allowed ?? MODEL_LAYER_FAILURE_MODES);
  const locator = buildLocator(doc);
  const foldCache: { level1?: Folded; level2?: Folded } = {};

  const accepted: Flag[] = [];
  const dropped: DroppedCandidate[] = [];
  const seen = new Set<string>();
  let counter = 0;

  const fm03 = evaluateFm03(doc);

  for (const candidate of candidates) {
    const id = candidate.failureModeId as FailureModeId;

    if (!allowed.has(id)) {
      dropped.push({
        failureModeId: candidate.failureModeId,
        quote: candidate.quote,
        reason: 'failure mode not in this layer',
      });
      continue;
    }

    if (!candidate.fires) {
      // Not a drop worth showing the user, but recorded so the counts add up.
      dropped.push({
        failureModeId: id,
        quote: candidate.quote,
        reason: 'reported as not firing',
      });
      continue;
    }

    if (candidate.quote === null || candidate.quote.trim().length === 0) {
      dropped.push({ failureModeId: id, quote: candidate.quote, reason: 'no quote supplied' });
      continue;
    }

    const quote = candidate.quote.trim();

    if (quote.length < MIN_QUOTE_CHARS) {
      dropped.push({
        failureModeId: id,
        quote,
        reason: 'quote too short to identify a clause',
      });
      continue;
    }
    if (quote.length > MAX_QUOTE_CHARS) {
      dropped.push({ failureModeId: id, quote, reason: 'quote longer than a clause' });
      continue;
    }

    const match = locateQuote(doc.rawText, quote, foldCache);
    if (!match) {
      dropped.push({
        failureModeId: id,
        quote,
        reason: 'quote not found in document',
        attempts: [
          'exact match',
          'whitespace normalised',
          'punctuation and ligatures normalised',
        ],
      });
      continue;
    }

    // FM-03 is a join, decided here rather than by the model.
    if (id === 'FM-03' && !fm03.fires) {
      dropped.push({
        failureModeId: id,
        quote,
        reason: 'FM-03 requires both of its two conditions',
        attempts: [
          `third party in the project structure: ${fm03.thirdPartyPresent ? `yes — "${fm03.thirdPartyEvidence}"` : 'no'}`,
          `witness role described: ${fm03.witnessRoleDescribed ? `yes — "${fm03.witnessEvidence}"` : 'no'}`,
          `witness role constrained by employer: ${fm03.employerConstrained ? `yes — "${fm03.employerEvidence}"` : 'no'}`,
        ],
      });
      continue;
    }

    const section = locator.sectionOf(match.start);
    const sectionRef = sectionRefOf(section);

    // One flag per failure mode per section, as elsewhere.
    const key = `${id}::${sectionRef}`;
    if (seen.has(key)) {
      dropped.push({
        failureModeId: id,
        quote,
        reason: 'reported as not firing',
        attempts: [`already flagged for ${id} in ${sectionRef}`],
      });
      continue;
    }
    seen.add(key);

    const signalIndex =
      candidate.basisIndex !== null && candidate.basisIndex >= 0 ? candidate.basisIndex : 0;

    accepted.push(
      enrichFlag({
        id: `flag-${CHECK_ID}-${++counter}`,
        failureModeId: id,
        // Tier comes from the taxonomy. The model does not get a say.
        tier: tierOf(id),
        layer: 'model-assisted',
        checkId: CHECK_ID,
        // Titles come from the taxonomy, never from the model.
        title: FAILURE_MODE_NAMES[id] ?? id,
        // The document's text at the located position, not the model's copy.
        quote: match.verbatim,
        sectionRef,
        page: locator.pageOf(match.start),
        // The gate already knows exactly where the quote is, so the document
        // pane's highlight comes free and is never a guess.
        quoteOffsets: locator.offsetsAt(match.start, match.end),
        basis: detectionSignal(id, signalIndex),
        evidenceCaseIds: evidenceFor(id),
        status: 'open',
      }),
    );
  }

  return { accepted, dropped };
}

/** Flag titles are the failure modes' own names, straight from the taxonomy. */
const FAILURE_MODE_NAMES: Record<string, string> = Object.fromEntries(
  TAXONOMY.failure_modes.map((fm) => [fm.id, fm.name]),
);

/** Counts for the report header. */
export function summariseDrops(dropped: DroppedCandidate[]): {
  total: number;
  quoteNotFound: number;
  byReason: { reason: string; count: number }[];
} {
  // "Reported as not firing" is the model correctly declining; it is not a drop
  // a reviewer needs to see, so it is excluded from the headline count.
  const meaningful = dropped.filter((d) => d.reason !== 'reported as not firing');
  const byReason = new Map<string, number>();
  for (const d of meaningful) byReason.set(d.reason, (byReason.get(d.reason) ?? 0) + 1);

  return {
    total: meaningful.length,
    quoteNotFound: meaningful.filter((d) => d.reason === 'quote not found in document').length,
    byReason: [...byReason.entries()].map(([reason, count]) => ({ reason, count })),
  };
}
